// @figma file=pkCGby54zXzTsccXvHqzO6 node=15769:74332 name="Corausel variants" set="mytaible-shadcn-studio" verified=2026-03-22

import useEmblaCarousel, { type UseEmblaCarouselType } from 'embla-carousel-react';
import { ArrowLeftIcon, ArrowRightIcon } from 'lucide-react';
import * as React from 'react';
import { cn } from '../../lib/utils';

// ─── Types ────────────────────────────────────────────────────────────────────

export type CarouselApi = UseEmblaCarouselType[1];
type UseCarouselParameters = Parameters<typeof useEmblaCarousel>;
type CarouselOptions = UseCarouselParameters[0];
type CarouselPlugin = UseCarouselParameters[1];

// ─── Context ──────────────────────────────────────────────────────────────────

type CarouselContextValue = {
  carouselRef: ReturnType<typeof useEmblaCarousel>[0];
  api: CarouselApi;
  scrollPrev: () => void;
  scrollNext: () => void;
  canScrollPrev: boolean;
  canScrollNext: boolean;
  orientation: 'horizontal' | 'vertical';
};

const CarouselContext = React.createContext<CarouselContextValue | null>(null);

export function useCarousel(): CarouselContextValue {
  const context = React.useContext(CarouselContext);
  if (!context) {
    throw new Error('useCarousel must be used within a <Carousel />');
  }
  return context;
}

// ─── Props ────────────────────────────────────────────────────────────────────

/**
 * Root props for the Carousel component.
 *
 * @property opts        - embla-carousel options (axis is derived from orientation)
 * @property plugins     - embla-carousel plugins array
 * @property orientation - scroll axis; defaults to 'horizontal'
 * @property setApi      - callback receiving the embla API instance after mount
 */
export interface CarouselProps extends React.HTMLAttributes<HTMLElement> {
  opts?: CarouselOptions;
  plugins?: CarouselPlugin;
  orientation?: 'horizontal' | 'vertical';
  setApi?: (api: CarouselApi) => void;
}

export interface CarouselContentProps extends React.HTMLAttributes<HTMLDivElement> {}
export interface CarouselItemProps extends React.HTMLAttributes<HTMLDivElement> {}
export interface CarouselPreviousProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {}
export interface CarouselNextProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {}

// ─── Carousel (root) ──────────────────────────────────────────────────────────

/**
 * Horizontal (or vertical) scrolling carousel built on embla-carousel.
 *
 * Keyboard navigation: `ArrowLeft` / `ArrowRight` scroll the carousel.
 * ARIA: `role="region"` + `aria-roledescription="carousel"` on the root.
 *
 * @example
 * ```tsx
 * <Carousel>
 *   <CarouselContent>
 *     <CarouselItem>Slide 1</CarouselItem>
 *     <CarouselItem>Slide 2</CarouselItem>
 *   </CarouselContent>
 *   <CarouselPrevious />
 *   <CarouselNext />
 * </Carousel>
 * ```
 */
const Carousel = React.forwardRef<HTMLElement, CarouselProps>(
  ({ orientation = 'horizontal', opts, setApi, plugins, className, children, ...props }, ref) => {
    const [carouselRef, api] = useEmblaCarousel(
      {
        ...opts,
        axis: orientation === 'horizontal' ? 'x' : 'y',
      },
      plugins,
    );

    const [canScrollPrev, setCanScrollPrev] = React.useState(false);
    const [canScrollNext, setCanScrollNext] = React.useState(false);

    const onSelect = React.useCallback((api: CarouselApi) => {
      if (!api) return;
      setCanScrollPrev(api.canScrollPrev());
      setCanScrollNext(api.canScrollNext());
    }, []);

    const scrollPrev = React.useCallback(() => {
      api?.scrollPrev();
    }, [api]);

    const scrollNext = React.useCallback(() => {
      api?.scrollNext();
    }, [api]);

    const handleKeyDown = React.useCallback(
      (event: React.KeyboardEvent<HTMLElement>) => {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          scrollPrev();
        } else if (event.key === 'ArrowRight') {
          event.preventDefault();
          scrollNext();
        }
      },
      [scrollPrev, scrollNext],
    );

    React.useEffect(() => {
      if (!api || !setApi) return;
      setApi(api);
    }, [api, setApi]);

    React.useEffect(() => {
      if (!api) return;
      onSelect(api);
      api.on('reInit', onSelect);
      api.on('select', onSelect);
      return () => {
        api?.off('reInit', onSelect);
        api?.off('select', onSelect);
      };
    }, [api, onSelect]);

    return (
      <CarouselContext.Provider
        value={{
          carouselRef,
          api,
          scrollPrev,
          scrollNext,
          canScrollPrev,
          canScrollNext,
          orientation,
        }}
      >
        <section
          ref={ref}
          onKeyDownCapture={handleKeyDown}
          className={cn('relative', className)}
          aria-label="carousel"
          aria-roledescription="carousel"
          {...props}
        >
          {children}
        </section>
      </CarouselContext.Provider>
    );
  },
);
Carousel.displayName = 'Carousel';

// ─── CarouselContent ──────────────────────────────────────────────────────────

/**
 * The scrollable track container. Place `<CarouselItem>` elements as direct
 * children. The component attaches the embla viewport ref automatically.
 */
const CarouselContent = React.forwardRef<HTMLDivElement, CarouselContentProps>(
  ({ className, ...props }, ref) => {
    const { carouselRef, orientation } = useCarousel();
    return (
      <div ref={carouselRef} className="overflow-hidden">
        <div
          ref={ref}
          className={cn(
            'flex',
            orientation === 'horizontal' ? '-ml-4' : '-mt-4 flex-col',
            className,
          )}
          {...props}
        />
      </div>
    );
  },
);
CarouselContent.displayName = 'CarouselContent';

// ─── CarouselItem ─────────────────────────────────────────────────────────────

/**
 * An individual slide within the carousel.
 *
 * ARIA: `role="group"` + `aria-roledescription="slide"`.
 */
const CarouselItem = React.forwardRef<HTMLDivElement, CarouselItemProps>(
  ({ className, ...props }, ref) => {
    const { orientation } = useCarousel();
    return (
      // biome-ignore lint/a11y/useSemanticElements: carousel slide uses role="group" per WAI-ARIA carousel pattern — <fieldset> semantics don't apply
      <div
        ref={ref}
        role="group"
        aria-roledescription="slide"
        className={cn(
          'min-w-0 shrink-0 grow-0 basis-full',
          orientation === 'horizontal' ? 'pl-4' : 'pt-4',
          className,
        )}
        {...props}
      />
    );
  },
);
CarouselItem.displayName = 'CarouselItem';

// ─── CarouselPrevious ─────────────────────────────────────────────────────────

/**
 * Previous-slide navigation button.
 *
 * Figma: Arrow buttons component set — Property 1=Left btn.
 * - 32×32px circle (rounded-full), bg-background, border-border, shadow-sm
 * - Hover: bg-muted  |  Disabled: opacity-50 (at first slide)
 */
const CarouselPrevious = React.forwardRef<HTMLButtonElement, CarouselPreviousProps>(
  ({ className, ...props }, ref) => {
    const { orientation, scrollPrev, canScrollPrev } = useCarousel();
    return (
      <button
        ref={ref}
        type="button"
        className={cn(
          // Layout — Figma: 32×32px circle
          'inline-flex items-center justify-center',
          'absolute h-8 w-8 rounded-full',
          // Colours — Figma: bg-background, border-border
          'bg-background border border-border',
          // Shadow — Figma: Light/box-shadow/default/xs
          'shadow-sm',
          // Hover — Figma: fill_OVH2HZ → bg-muted
          'hover:bg-muted',
          // Position — left side, vertically centred for horizontal
          orientation === 'horizontal'
            ? '-left-12 top-1/2 -translate-y-1/2'
            : '-top-12 left-1/2 -translate-x-1/2 rotate-90',
          // Focus ring
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
          // Disabled — Figma: opacity 0.5
          'disabled:pointer-events-none disabled:opacity-50',
          // Transition
          'transition-colors',
          className,
        )}
        disabled={!canScrollPrev}
        onClick={scrollPrev}
        aria-label="Previous slide"
        {...props}
      >
        <ArrowLeftIcon className="size-4 text-foreground" aria-hidden="true" />
      </button>
    );
  },
);
CarouselPrevious.displayName = 'CarouselPrevious';

// ─── CarouselNext ─────────────────────────────────────────────────────────────

/**
 * Next-slide navigation button.
 *
 * Figma: Arrow buttons component set — Property 1=Right btn.
 * - 32×32px circle (rounded-full), bg-background, border-border, shadow-sm
 * - Hover: bg-muted  |  Disabled: opacity-50 (at last slide)
 */
const CarouselNext = React.forwardRef<HTMLButtonElement, CarouselNextProps>(
  ({ className, ...props }, ref) => {
    const { orientation, scrollNext, canScrollNext } = useCarousel();
    return (
      <button
        ref={ref}
        type="button"
        className={cn(
          'inline-flex items-center justify-center',
          'absolute h-8 w-8 rounded-full',
          'bg-background border border-border',
          'shadow-sm',
          'hover:bg-muted',
          orientation === 'horizontal'
            ? '-right-12 top-1/2 -translate-y-1/2'
            : '-bottom-12 left-1/2 -translate-x-1/2 rotate-90',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
          'disabled:pointer-events-none disabled:opacity-50',
          'transition-colors',
          className,
        )}
        disabled={!canScrollNext}
        onClick={scrollNext}
        aria-label="Next slide"
        {...props}
      >
        <ArrowRightIcon className="size-4 text-foreground" aria-hidden="true" />
      </button>
    );
  },
);
CarouselNext.displayName = 'CarouselNext';

// ─── Exports ──────────────────────────────────────────────────────────────────

export { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious };
